/**
 * <ul
 *    class="mb-5 flex list-none flex-row flex-wrap border-b border-ops-border pl-0 gap-x-8"
 *    role="tablist"
 *    data-br-tabs>
 *
 *    <li role="presentation">
 *        <a
 *                href="#tabs-{{ $savedItemList[0]->parent_category_id }}"
 *                class="block border-0 px-10 pb-2.5 pt-3 x-sm:py-1 x-sm:px-2
 *                bg-white x-sm:bg-transparent
 *                font-medium uppercase leading-tight text-ops-night
 *                hover:isolate hover:brightness-90 x-sm:hover:underline
 *                focus:isolate
 *                data-[br-tabs-active]:bg-ops-night data-[br-tabs-active]:text-white"
 *                data-br-tab
 *                data-br-target="#id-of-container-the-tab-should-open"
 *                role="tab"
 *                aria-controls="id-of-container-the-tab-should-open"
 *                aria-selected="true or false depending on which tab you want open when the page loads"
 *        >Tab name goes here</a>
 *    </li>
 *    <li> ... </li>
 * </ul>
 */

import { addListener, removeListener } from "./support/utils";

class BRTabs
{
    // Registry to hold widget instances indexed by DOM elements
    static tabsInstances = new Map();

    constructor(tabsContainer) {
        if (tabsContainer && !BRTabs.getInstance(tabsContainer)) {
            this.tabsContainer = tabsContainer; // Store the tabsContainer
            this.tabsElements = [];
            this.instanceEventListeners = new Map();

            tabsContainer.querySelectorAll('[data-br-tab]').forEach((tabElement) => {
                this.addTab(tabElement);
            });

            BRTabs.tabsInstances.set(tabsContainer, this);
        }
    }

    addTab(tabElement)
    {
        const isActive = tabElement.hasAttribute('data-br-tab-active');
        const tabContent = document.querySelector(tabElement.getAttribute('data-br-target'));
        if (isActive) {
            tabContent.classList.remove('hidden');
        }

        this.tabsElements.push({
                                   tab: tabElement,
                                   container: tabContent,
                                   is_active: isActive,
                               });

        this.setListener('click', tabElement, this.changeTabs.bind(this));
    }

    setListener(event, elements, method)
    {
        if (elements instanceof NodeList || Array.isArray(elements)) {
            elements.forEach(element => this._addListenerToElement(event, element, method));
        }
        else {
            this._addListenerToElement(event, elements, method);
        }
    }

    _addListenerToElement(event, element, method)
    {
        addListener(event, element, method);

        // Check if the element already has listeners stored
        if (!this.instanceEventListeners.has(element)) {
            this.instanceEventListeners.set(element, new Map());
        }

        const listenersMap = this.instanceEventListeners.get(element);

        // Use a combination of event type and method as the key for the inner map
        // This assumes we won't add the exact same function twice as a listener for the same event type on the same element
        const eventKey = `${event}_${method.name}`;

        listenersMap.set(eventKey, method);
    }

    changeTabs(evt)
    {
        evt.preventDefault();
        let tab = evt.currentTarget;

        this.tabsElements.forEach((tabsElement) => {
            if (tabsElement.is_active) {
                tabsElement.tab.removeAttribute('data-br-tab-active');
                tabsElement.container.removeAttribute('data-br-tab-active');
                tabsElement.container.classList.add('hidden');
                tabsElement.container.classList.add('opacity-0');
                tabsElement.is_active = 0;
            }
            if (tabsElement.tab === tab) {
                tabsElement.tab.setAttribute('data-br-tab-active', '');
                tabsElement.container.setAttribute('data-br-tab-active', '');
                tabsElement.container.classList.remove('hidden');
                tabsElement.container.classList.remove('opacity-0');
                tabsElement.is_active = 1;
            }
        });
    }

    dispose() {
        // Remove all event listeners associated with this instance
        for (const [element, listenersMap] of this.instanceEventListeners) {
            for (const [event, method] of listenersMap) {
                if (method.length > 0) {
                    removeListener(event, element, method);
                }
            }
        }

        // Clear the instanceEventListeners map
        this.instanceEventListeners.clear();
        this.instanceEventListeners = null;

        // Remove this instance from the tabsInstances map
        BRTabs.tabsInstances.delete(this.tabsContainer);

        // Clear other references
        this.tabsElements = null;
        this.tabsContainer = null;
    }

    static initAll(parentElement)
    {
        let container = parentElement || document;
        container.querySelectorAll('[data-br-tabs]').forEach(tabsContainer => {
            if (!BRTabs.tabsInstances.has(tabsContainer)) {
                new BRTabs(tabsContainer);
            }
        });
    }

    // Static method to access a widget instance from a DOM element
    static getInstance(tabsElement) {
        return BRTabs.tabsInstances.get(tabsElement);
    }
}

export default BRTabs;